import React, { ReactElement, useMemo } from 'react';

import { Box, createStyles, makeStyles, Popover, Theme, Typography } from '@material-ui/core';

import { monochrome } from '@vyce/core/src/theme/styles';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';

interface Props {
  children: ReactElement;
  check_in_ok?: boolean;
  check_out_ok?: boolean;
  face_out_ok?: boolean;
  face_in_ok?: boolean;
  auto_clock_out?: boolean;
  clock_in_now?: boolean;
  minutesLate?: number;
  minutesEarly?: number;
}

export const TimeLogWarningCell: React.FC<Props> = ({
  children,
  face_in_ok,
  face_out_ok,
  check_out_ok,
  check_in_ok,
  auto_clock_out,
  clock_in_now = false,
  minutesLate = 0,
  minutesEarly = 0,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const classes = useStyles();

  const hasWarnings =
    check_in_ok === false ||
    check_out_ok === false ||
    face_out_ok === false ||
    face_in_ok === false ||
    auto_clock_out ||
    clock_in_now;

  const isLate = useMemo(() => minutesLate > 10, [minutesLate]);
  const isEarly = useMemo(() => minutesEarly > 10, [minutesEarly]);

  const getWarningTooltipText = useMemo((): string[] => {
    let warnings = [];
    if (clock_in_now) {
      return ['Currently Clocked In.'];
    }
    if (face_in_ok === false) {
      warnings.push('Clock In FaceMatch not verified');
    }
    if (face_out_ok === false) {
      warnings.push('Clock Out FaceMatch not verified');
    }
    if (check_in_ok === false) {
      warnings.push('Clocked In outside of work location');
    }
    if (check_out_ok === false) {
      warnings.push('Clocked Out outside of work location');
    }
    if (auto_clock_out) {
      warnings.push('Automated Clocked Out');
    }
    if (isLate) {
      if (minutesLate > 60) {
        const hours = Math.floor(minutesLate / 60);
        const minutes = minutesLate % 60;
        warnings.push(`Clocked In late by ${hours}h ${minutes}m`);
      } else {
        warnings.push(`Clocked In late by ${minutesLate} minutes`);
      }
    }
    if (isEarly) {
      if (minutesEarly > 60) {
        const hours = Math.floor(minutesEarly / 60);
        const minutes = minutesEarly % 60;
        warnings.push(`Clocked Out early by ${hours}h ${minutes}m`);
      } else {
        warnings.push(`Clocked Out early by ${minutesEarly} minutes`);
      }
    }
    return warnings;
  }, [face_out_ok, check_out_ok, check_in_ok, face_in_ok, auto_clock_out, clock_in_now, isLate, isEarly]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasWarnings && minutesLate <= 10 && minutesEarly <= 10) {
      return;
    }
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Box
        borderRadius={16}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        className={
          hasWarnings && !clock_in_now
            ? classes.warningContainer
            : isLate || isEarly
            ? classes.isLate
            : undefined
        }
      >
        {children}
      </Box>

      <Popover
        id="mouse-over-popover"
        container={document.getElementById(MAIN_CONTAINER_ID)}
        className={classes.popover}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box className={classes.content}>
          {getWarningTooltipText.map(item => (
            <Typography key={item} className={classes.text} variant="caption">
              {item}
            </Typography>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    warningContainer: {
      lineHeight: '24px',
      background: theme.palette.warning.light,
      border: `1px solid ${theme.palette.warning.main}`,
      padding: '4px 8px',
      width: 'fit-content',
    },
    isLate: {
      border: `1px solid ${theme.palette.warning.main}`,
      padding: '4px 8px',
      width: 'fit-content',
    },
    popover: {
      pointerEvents: 'none',
    },
    content: {
      padding: '10px 16px',
      display: 'flex',
      flexDirection: 'column',
    },
    text: {
      color: theme.palette.type === 'dark' ? monochrome.mediumlight : monochrome.dark,
    },
  })
);
